import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useStore } from 'react-redux';
import {
  Button,
  Chip,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import moment from 'moment';
import Area from '../../../fields/area';
import Statuses from '../../../fields/status.rider';
import { RIDER_STATUSES } from './utils';

export const useStyles = makeStyles((theme) => ({
  fromControl: {
    width: '100%',
  },
  hidePrint: {},
  '@media print': {
    hidePrint: {
      display: 'none',
    },
  },
}));

export type ParcelDate = {
  startDate?: string;
  endDate?: string;
};

export type FiltersProps = {
  loading: boolean;
  getStatus: string[];
  setStatus: React.Dispatch<React.SetStateAction<string[]>>;
  getParcelDate: ParcelDate;
  setParcelDate: React.Dispatch<React.SetStateAction<ParcelDate>>;
  area: any;
  setArea: React.Dispatch<React.SetStateAction<any>>;
  fetchAPI: () => Promise<void>;
  handlePrint: () => void;
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  showBulkActions: boolean;
  setShowBulkActions: React.Dispatch<React.SetStateAction<boolean>>;
  statusIDSetAction: string | null;
  setStatusIDSetAction: React.Dispatch<React.SetStateAction<string | null>>;
  getPaymentDate: string;
  setPaymentDate: React.Dispatch<React.SetStateAction<string>>;
  updateCheckboxStatus: () => Promise<void>;
  set_search: React.Dispatch<React.SetStateAction<string>>;
  get_search: string;
};

const riderStatusOptions = Object.entries(RIDER_STATUSES).map(([value, label]) => ({
  value,
  label,
}));

export default function RiderFilters({
  loading,
  getStatus,
  setStatus,
  getParcelDate,
  setParcelDate,
  area,
  setArea,
  fetchAPI,
  handlePrint,
  showFilters,
  setShowFilters,
  showBulkActions,
  setShowBulkActions,
  statusIDSetAction,
  setStatusIDSetAction,
  getPaymentDate,
  setPaymentDate,
  updateCheckboxStatus,
  set_search,
  get_search,
}: FiltersProps) {
  const classes = useStyles();
  const store = useStore();

  return (
    <>
      <div style={{ gap: 12, marginBottom: 12 }} className="flex md:hidden">
        <Button variant="outlined" size="small" onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
        <Button variant="outlined" size="small" onClick={() => setShowBulkActions((v) => !v)}>
          {showBulkActions ? 'Hide Bulk Actions' : 'Show Bulk Actions'}
        </Button>
      </div>

      <div
        style={{
          background: '#f7f9fb',
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
          boxShadow: '0 2px 8px #e3e8ee',
        }}
        className={`${showFilters ? 'block' : 'hidden'} md:block`}
      >
        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Search Filters</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <TextField
              disabled={loading}
              label="Search"
              fullWidth
              variant="outlined"
              size="small"
              onChange={(e) => {
                set_search(e?.target.value);
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <TextField
              label="Booking Date"
              type="date"
              variant="outlined"
              size="small"
              value={getParcelDate.startDate}
              onChange={(e) => {
                let newDate: ParcelDate = {};
                if (moment(e.target.value).isValid()) {
                  newDate = {
                    ...getParcelDate,
                    startDate: moment(e.target.value).format('YYYY-MM-DD').toString(),
                  };
                } else {
                  newDate = { startDate: undefined, endDate: undefined };
                }
                setParcelDate(newDate);
              }}
              InputLabelProps={{ shrink: true }}
              inputRef={(input) => {
                if (input) {
                  (input as HTMLInputElement).showPicker = (input as HTMLInputElement).showPicker || (() => {});
                }
              }}
              onClick={(e) => {
                const input = e.target as HTMLInputElement;
                if (input && typeof input.showPicker === 'function') {
                  input.showPicker();
                }
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <TextField
              label="Booking End"
              type="date"
              variant="outlined"
              size="small"
              disabled={!getParcelDate.startDate}
              value={getParcelDate.endDate ?? ''}
              onChange={(e) => {
                let newDate: ParcelDate = {};
                if (moment(e.target.value).isValid()) {
                  newDate = {
                    ...getParcelDate,
                    endDate: moment(e.target.value).format('YYYY-MM-DD').toString(),
                  };
                } else {
                  newDate = { ...getParcelDate, endDate: undefined };
                }
                setParcelDate(newDate);
              }}
              InputLabelProps={{ shrink: true }}
              inputRef={(input) => {
                if (input) {
                  (input as HTMLInputElement).showPicker = (input as HTMLInputElement).showPicker || (() => {});
                }
              }}
              onClick={(e) => {
                const input = e.target as HTMLInputElement;
                if (input && typeof input.showPicker === 'function') {
                  input.showPicker();
                }
              }}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <Statuses
              size="small"
              options={riderStatusOptions}
              onChange={(cp: string[] | null) => setStatus(cp ?? [])}
              defaultValue={getStatus}
              multiple
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <Area disabled={loading} onChange={(cp: any) => setArea(cp ?? null)} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {loading ? (
              <LoadingButton loading variant="contained" loadingPosition="end" sx={{ minWidth: 100 }}>
                Searching..
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  await fetchAPI();
                }}
                sx={{ minWidth: 100 }}
              >
                Search
              </Button>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          background: '#f7f9fb',
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          boxShadow: '0 2px 8px #e3e8ee',
          flexDirection: 'column',
          maxWidth: 320,
        }}
        className={`${showBulkActions ? 'flex' : 'hidden'} md:flex`}
      >
        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Bulk Actions</div>
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel id="set-status-label">Set Status</InputLabel>
          <Select
            disabled={loading}
            label="Set Status"
            value={statusIDSetAction ?? ''}
            onChange={(e) => {
              const status: any = e.target.value ?? '';
              setStatusIDSetAction(status);
            }}
            className={classes.fromControl}
            labelId="set-status-label"
          >
            {Object.keys(RIDER_STATUSES).map((v: any, index: number) => (
              <MenuItem key={`${v}-${index}-Select-item`} value={v}>
                {RIDER_STATUSES[v]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {statusIDSetAction && ['DELIVERED', 'PARTIALLY_DELIVERED', 'RETURNED'].includes(statusIDSetAction) ? (
          <TextField
            label="Payment Date"
            type="date"
            fullWidth
            size="small"
            variant="outlined"
            value={getPaymentDate}
            onChange={(e) => {
              setPaymentDate(moment(e.target.value).format('YYYY-MM-DD').toString());
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ marginTop: 1 }}
          />
        ) : null}
        <Button
          color="primary"
          onClick={() => {
            updateCheckboxStatus();
          }}
          variant="contained"
          size="small"
          disabled={loading ?? !statusIDSetAction}
          sx={{ marginTop: 1, width: '100%' }}
        >
          Set Status
        </Button>
      </div>
    </>
  );
}
